/**
 * Phone layout for data tables.
 *
 * Copies each column header onto its body cells as `data-label` and marks the
 * table with `data-rt="stack"`, so CSS can render rows as labeled cards on small
 * screens. Attributes are used instead of classes because React rewrites
 * `className` on re-render but leaves unmanaged attributes alone.
 */
const TABLE_SELECTOR = "table.ops-table, table.history-data-table";

/** Dense side-by-side financial grids stay horizontally scrollable. */
const KEEP_SCROLL_SELECTOR =
  ".ops-table--compact-fin, .ops-table--split-zones, .ops-table--admin-projects, [data-rt-keep-scroll]";

const BLOCK_CONTENT_SELECTOR = "div, ul, ol, dl, table, form, textarea";

/** One text run or one element: safe to sit beside its label in a two-column row. */
function isSingleValueCell(cell) {
  let parts = 0;
  for (const node of cell.childNodes) {
    if (node.nodeType === 1 || (node.nodeType === 3 && node.textContent.trim())) parts += 1;
    if (parts > 1) return false;
  }
  return !cell.querySelector(BLOCK_CONTENT_SELECTOR);
}

function headerLabels(table) {
  const head = table.tHead;
  if (!head || !head.rows.length) return null;
  const labels = [];
  for (const th of head.rows[head.rows.length - 1].cells) {
    const text = (th.textContent || "").replace(/\s+/g, " ").trim();
    for (let i = 0; i < (th.colSpan || 1); i += 1) labels.push(text);
  }
  return labels;
}

function setAttr(el, name, value) {
  if (value == null) {
    if (el.hasAttribute(name)) el.removeAttribute(name);
  } else if (el.getAttribute(name) !== value) {
    el.setAttribute(name, value);
  }
}

function labelTable(table) {
  if (table.matches(KEEP_SCROLL_SELECTOR)) return;
  const labels = headerLabels(table);
  if (!labels) return;
  setAttr(table, "data-rt", "stack");
  for (const body of table.tBodies) {
    for (const row of body.rows) {
      let col = 0;
      for (const cell of row.cells) {
        const span = cell.colSpan || 1;
        const isFullRow = span > 1 && row.cells.length === 1;
        setAttr(cell, "data-rt-full", isFullRow ? "" : null);
        if (!isFullRow) {
          // `data-rt-label` marks labels we wrote; any other `data-label` came from the component.
          const hasOwnLabel = cell.hasAttribute("data-label") && !cell.hasAttribute("data-rt-label");
          if (!hasOwnLabel) {
            const label = labels[col] || null;
            setAttr(cell, "data-label", label);
            setAttr(cell, "data-rt-label", label ? "" : null);
          }
          setAttr(cell, "data-rt-inline", isSingleValueCell(cell) ? "" : null);
        }
        col += span;
      }
    }
  }
}

export function installResponsiveTables(root = document.body) {
  if (typeof window === "undefined" || !root || !("MutationObserver" in window)) return () => {};
  let frame = 0;
  const run = () => {
    frame = 0;
    root.querySelectorAll(TABLE_SELECTOR).forEach(labelTable);
  };
  const schedule = () => {
    if (!frame) frame = window.requestAnimationFrame(run);
  };
  const observer = new MutationObserver(schedule);
  observer.observe(root, { childList: true, subtree: true, characterData: true });
  run();
  return () => {
    observer.disconnect();
    if (frame) window.cancelAnimationFrame(frame);
  };
}
